import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	runTransaction,
	Timestamp,
	updateDoc,
	orderBy,
} from 'firebase/firestore';
import { firestore, storage } from '@/utils/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import BASE_QUERY from '@/features/baseQuery';
import { getActingUser } from '../user/userApi';
import { dealsApi } from '@/features/deals/dealsApi';
import {
	STORAGE_PURPOSES,
	clientStoragePath,
	dealStoragePath,
	relationshipCompanyStoragePath,
} from '@/utils/storagePaths';

const WORKSPACE_COLLECTION = 'workspaces';
const VERSIONS_SUBCOLLECTION = 'versions';

// A workspace belongs to a deal, a relationship company, or a client, never more than one.
// These are the getWorkspacePage cache args, so mutations patch the same cache entry.
const getWorkspaceArgs = ({ dealId, clientId, relationshipCompanyId }) => {
	if (dealId) return { dealId };
	if (relationshipCompanyId) return { relationshipCompanyId };
	return { clientId };
};

// Every workspace lives at `deal-{dealId}`, `relationshipCompany-{id}`, or `client-{clientId}`.
const getWorkspaceDocId = ({ dealId, clientId, relationshipCompanyId }) => {
	if (dealId) return `deal-${dealId}`;
	if (relationshipCompanyId) return `relationshipCompany-${relationshipCompanyId}`;
	return `client-${clientId}`;
};

const getWorkspaceVersionFileName = () => {
	const stamp = new Date().toISOString().replace(/[:.]/g, '-');
	return `content-${stamp}.html`;
};

// Resolves the workspace doc from Firestore only, without touching storage, so
// callers that just need the doc ID aren't blocked by a missing/corrupt blob.
const resolveWorkspaceDoc = async ({ dealId, clientId, relationshipCompanyId }) => {
	const snapshot = await getDoc(
		doc(
			firestore,
			WORKSPACE_COLLECTION,
			getWorkspaceDocId({ dealId, clientId, relationshipCompanyId }),
		),
	);

	if (!snapshot.exists()) {
		return null;
	}

	return { id: snapshot.id, data: snapshot.data() };
};

const fetchWorkspaceHtml = async (filePath) => {
	if (!filePath) return '';
	const normalizedPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
	const downloadUrl = await getDownloadURL(ref(storage, normalizedPath));
	const response = await fetch(downloadUrl);
	return response.text();
};

export const workspacesApi = createApi({
	reducerPath: 'workspacesApi',
	tagTypes: ['WORKSPACE', 'WORKSPACE_VERSIONS'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getWorkspacePage: builder.query({
			providesTags: (result, error, arg) => [{ type: 'WORKSPACE', id: getWorkspaceDocId(arg) }],
			queryFn: async ({ dealId, clientId, relationshipCompanyId }) => {
				try {
					const workspaceDoc = await resolveWorkspaceDoc({
						dealId,
						clientId,
						relationshipCompanyId,
					});

					if (!workspaceDoc) {
						return { data: null };
					}

					const { id: workspaceId, data: workspaceData } = workspaceDoc;

					let filePath = workspaceData?.filePath;
					if (filePath?.startsWith('/')) {
						filePath = filePath.slice(1);
					}

					let html = '';

					console.log('Workspace data:', workspaceData);
					if (workspaceData?.filePath) {
						const storageRef = ref(storage, filePath);
						const downloadUrl = await getDownloadURL(storageRef);
						const response = await fetch(downloadUrl);
						html = await response.text();
					}

					return {
						data: {
							id: workspaceId,
							...JSON.parse(JSON.stringify(workspaceData)),
							html,
						},
					};
				} catch (err) {
					console.log('Error getting workspace page', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
		}),
		saveWorkspacePage: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'WORKSPACE_VERSIONS', id: getWorkspaceDocId(arg) },
			],
			queryFn: async (
				{ dealId, clientId, relationshipCompanyId, html },
				{ dispatch, getState },
			) => {
				const contextField = getWorkspaceArgs({ dealId, clientId, relationshipCompanyId });
				// Captured once so a save that started earlier can never win a race
				// against a save that started later but happens to finish first.
				const requestedAt = Timestamp.now();
				try {
					// User should always be valid because it's the logged in user
					const userData = await getActingUser(dispatch, getState);
					const userRef = doc(firestore, 'user', userData.userId);

					// Always target the deterministic ID: concurrent first-time saves for
					// the same object then independently land on the same doc path
					// instead of racing to create duplicates via addDoc's random IDs.
					const firebaseDocId = getWorkspaceDocId(contextField);

					const fileName = getWorkspaceVersionFileName();
					let filePath;
					if (dealId) {
						// A deal's workspace sits under its client, so read the deal's client.
						const dealRow = await dispatch(
							dealsApi.endpoints.getDealRow.initiate(dealId, { subscribe: false }),
						).unwrap();
						filePath = dealStoragePath(
							{ clientDocId: dealRow?.clientDocId, dealDocId: dealId },
							STORAGE_PURPOSES.workspaces,
							fileName,
						);
					} else if (relationshipCompanyId) {
						filePath = relationshipCompanyStoragePath(
							relationshipCompanyId,
							STORAGE_PURPOSES.workspaces,
							fileName,
						);
					} else {
						filePath = clientStoragePath(clientId, STORAGE_PURPOSES.workspaces, fileName);
					}

					const file = new File([html ?? ''], fileName, {
						type: 'text/html',
					});

					const storageRef = ref(storage, filePath);

					await uploadBytes(storageRef, file, {
						customMetadata: {
							...(dealId
								? { 'Deal ID': dealId }
								: relationshipCompanyId
									? { 'Relationship Company ID': relationshipCompanyId }
									: { 'Client ID': clientId }),
							'Firebase Document ID': firebaseDocId,
							'User ID': userData.userId,
						},
					});

					await addDoc(
						collection(
							firestore,
							WORKSPACE_COLLECTION,
							firebaseDocId,
							VERSIONS_SUBCOLLECTION,
						),
						{
							fileName,
							filePath,
							dateCreated: requestedAt,
						},
					);

					const workspaceDocRef = doc(firestore, WORKSPACE_COLLECTION, firebaseDocId);

					// Guard against out-of-order completion: if a save that started later
					// has already created/updated the doc, don't let this older save
					// clobber it. Since two concurrent first-time saves resolve to the
					// same deterministic doc ref, this transaction is also what stops
					// them from creating duplicate docs (Firestore serializes and
					// retries transactions that touch the same document).
					await runTransaction(firestore, async (transaction) => {
						const currentSnapshot = await transaction.get(workspaceDocRef);
						const currentUpdatedDate = currentSnapshot.data()?.updatedDate;

						if (
							currentUpdatedDate &&
							currentUpdatedDate.toMillis() > requestedAt.toMillis()
						) {
							return;
						}

						if (!currentSnapshot.exists()) {
							transaction.set(workspaceDocRef, {
								fileName,
								filePath,
								createdDate: requestedAt,
								updatedDate: requestedAt,
								createdBy: userRef,
								updatedBy: userRef,
								...contextField,
							});
							return;
						}

						transaction.update(workspaceDocRef, {
							fileName,
							filePath,
							updatedDate: requestedAt,
							updatedBy: userRef,
						});
					});

					// Patch the getWorkspacePage cache directly with the content we just wrote,
					// instead of invalidating it: a tag-invalidated refetch re-downloads the file
					// from storage independently of this mutation, and if two saves happen in quick
					// succession, an older refetch can resolve after a newer one and clobber it with
					// stale content. We already know the true current state here, so there's nothing
					// to race.
					dispatch(
						workspacesApi.util.updateQueryData(
							'getWorkspacePage',
							contextField,
							(draft) => ({
								...(draft ?? {}),
								id: firebaseDocId,
								fileName,
								filePath,
								html: html ?? '',
								...contextField,
							}),
						),
					);

					return {
						data: {
							id: firebaseDocId,
							fileName,
							filePath,
							...contextField,
						},
					};
				} catch (err) {
					console.log('Error saving workspace page', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
		}),
		getWorkspaceVersions: builder.query({
			providesTags: (result, error, arg) => [
				{ type: 'WORKSPACE_VERSIONS', id: getWorkspaceDocId(arg) },
			],
			queryFn: async ({ dealId, clientId, relationshipCompanyId }) => {
				try {
					const workspaceDoc = await resolveWorkspaceDoc({
						dealId,
						clientId,
						relationshipCompanyId,
					});

					if (!workspaceDoc) {
						return { data: [] };
					}

					const firebaseDocId = workspaceDoc.id;

					const versionsQuery = query(
						collection(
							firestore,
							WORKSPACE_COLLECTION,
							firebaseDocId,
							VERSIONS_SUBCOLLECTION,
						),
						orderBy('dateCreated', 'desc'),
					);
					const versionsSnapshot = await getDocs(versionsQuery);

					const versions = versionsSnapshot.docs.map((versionDoc) => {
						const data = versionDoc.data();
						return {
							id: versionDoc.id,
							fileName: data.fileName,
							filePath: data.filePath,
							createdAt: data.dateCreated?.toDate().toISOString() ?? null,
						};
					});

					return { data: versions };
				} catch (err) {
					console.log('Error listing workspace versions', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
		}),
		restoreWorkspacePage: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'WORKSPACE_VERSIONS', id: getWorkspaceDocId(arg) },
			],
			queryFn: async (
				{ dealId, clientId, relationshipCompanyId, versionId, fileName, filePath },
				{ dispatch, getState },
			) => {
				try {
					const workspaceDoc = await resolveWorkspaceDoc({
						dealId,
						clientId,
						relationshipCompanyId,
					});

					if (!workspaceDoc) {
						return {
							error: {
								status: 404,
								data: 'Workspace not found',
							},
						};
					}

					const firebaseDocId = workspaceDoc.id;

					const userData = await getActingUser(dispatch, getState);
					const userRef = doc(firestore, 'user', userData.userId);

					let nextFilePath = filePath;
					if (nextFilePath?.startsWith('/')) {
						nextFilePath = nextFilePath.slice(1);
					}

					const restoredAt = Timestamp.now();

					await updateDoc(doc(firestore, WORKSPACE_COLLECTION, firebaseDocId), {
						fileName,
						filePath: nextFilePath,
						updatedDate: restoredAt,
						...(userRef && {
							updatedBy: userRef,
						}),
					});

					if (versionId) {
						try {
							await updateDoc(
								doc(
									firestore,
									WORKSPACE_COLLECTION,
									firebaseDocId,
									VERSIONS_SUBCOLLECTION,
									versionId,
								),
								{ lastRestored: restoredAt },
							);
						} catch (versionErr) {
							// Best-effort — the restore itself already succeeded above.
							console.log('Error stamping restored version doc', versionErr);
						}
					}

					// Patch the getWorkspacePage cache directly with the restored content,
					// same reasoning as saveWorkspacePage: an invalidation-triggered refetch
					// races independently of this mutation and can resolve out of order.
					const html = await fetchWorkspaceHtml(nextFilePath);
					dispatch(
						workspacesApi.util.updateQueryData(
							'getWorkspacePage',
							getWorkspaceArgs({ dealId, clientId, relationshipCompanyId }),
							(draft) => ({
								...(draft ?? {}),
								id: firebaseDocId,
								fileName,
								filePath: nextFilePath,
								html,
							}),
						),
					);

					return {
						data: {
							id: firebaseDocId,
							fileName,
							filePath: nextFilePath,
						},
					};
				} catch (err) {
					console.log('Error restoring workspace page', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
		}),
	}),
});

export const {
	useGetWorkspacePageQuery,
	useSaveWorkspacePageMutation,
	useGetWorkspaceVersionsQuery,
	useRestoreWorkspacePageMutation,
} = workspacesApi;
